import React, { FC, useCallback } from 'react';
import { t } from '@lingui/core/macro';
import styled from 'styled-components';

import KEYS from '@rover/constants/src/platform/keys.constants';
import ClearSmall from '@rover/icons/svg/clear-small.svg';
import { Flex } from '@rover/kibble/core';

import { shouldBlurElement } from '../utils';

import LoadingIndicator from './LoadingIndicator';

// fine to use a custom button here its very unique to this component
// eslint-disable-next-line rover/prefer-kibble-components
const StyledClearButton = styled.button`
  border: none;
  padding: 0;
  display: flex;
  background-color: transparent;
  cursor: pointer;
  touch-action: none;
`;

type ClearButtonProps = {
  loading?: boolean;
  inputRef?: HTMLInputElement | null;
  blurInput: () => void;
  onClick?: () => void;
};

const ClearButton: FC<ClearButtonProps> = ({ loading = false, inputRef, blurInput, onClick }) => {
  const onClearTextButtonClick = useCallback(
    (event: React.MouseEvent | React.TouchEvent | React.KeyboardEvent): void => {
      event.preventDefault();
      event.stopPropagation();
      onClick?.();
    },
    [onClick]
  );

  const handleClearButtonKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      e.stopPropagation();
      if (e.key === KEYS.ENTER || e.key === KEYS.SPACE) {
        onClick?.();
      }
    },
    [onClick]
  );

  const handleClearButtonBlur = useCallback(
    (e: React.FocusEvent<HTMLButtonElement>): void => {
      if (shouldBlurElement(e) && e.relatedTarget !== inputRef) {
        blurInput();
      }
    },
    [blurInput, inputRef]
  );

  const topPosition = inputRef ? `${Math.round(inputRef.offsetHeight / 2)}px` : '50%';

  return (
    <Flex position="absolute" right="2x" top={topPosition} sx={{ transform: 'translateY(-50%)' }}>
      {loading ? (
        <LoadingIndicator dataTestId="loading-indicator" />
      ) : (
        <StyledClearButton
          type="button"
          id="clear-text-button"
          data-testid="clear-text-button"
          aria-label={t`clear input text`}
          tabIndex={0}
          onClick={onClearTextButtonClick}
          onPointerDown={onClearTextButtonClick}
          onKeyDown={handleClearButtonKeyDown}
          onBlur={handleClearButtonBlur}
        >
          <ClearSmall height={24} width={24} />
        </StyledClearButton>
      )}
    </Flex>
  );
};

export default ClearButton;
